// Smart Daara - Dashboard Layout (responsive)
import React, { useState } from 'react';
import { Outlet, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Navbar } from './Navbar';
import { Sidebar } from './Sidebar';
import { TenantAccessGate } from '../shared/TenantAccessGate';
import { cn } from '../../lib/utils';

export const DashboardLayout = ({ requiredRoles = [] }) => {
  const { user, isAuthenticated, loading } = useAuth();
  const { tenantSlug } = useParams();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRoles.length > 0 && !requiredRoles.includes(user?.role)) {
    return <Navigate to="/" replace />;
  }

  // If the URL contains a tenant slug, ensure it matches the current user's tenant.
  // super_admin doesn't use slugged URLs, but if they navigate to one we let them see it as-is.
  if (tenantSlug && user?.role !== 'super_admin' && user?.tenant_slug && tenantSlug !== user.tenant_slug) {
    // Redirect to the correct slug for this user, preserving the sub-area
    const parts = window.location.pathname.split('/').filter(Boolean); // e.g. ['wrong-slug', 'school', 'classes']
    const restOfPath = parts.slice(1).join('/');
    return <Navigate to={`/${user.tenant_slug}/${restOfPath}`} replace />;
  }

  return (
    <TenantAccessGate>
      <div className="min-h-screen bg-slate-50">
        <Navbar onMenuClick={() => setMobileOpen(true)} />
        <div className="flex">
          <Sidebar
            collapsed={sidebarCollapsed}
            onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
            mobileOpen={mobileOpen}
            onMobileClose={() => setMobileOpen(false)}
          />
          <main
            className={cn(
              "flex-1 min-w-0 min-h-[calc(100vh-4rem)] transition-all duration-300 p-3 sm:p-4 md:p-6 overflow-x-hidden",
              // Push right on desktop only; on mobile the sidebar is off-canvas
              sidebarCollapsed ? "md:ml-16" : "md:ml-64"
            )}
            data-testid="dashboard-content"
          >
            <Outlet />
          </main>
        </div>
      </div>
    </TenantAccessGate>
  );
};

export default DashboardLayout;
